import React, { useState, useEffect, useMemo } from 'react';
import { type Project, createProject, updateProject } from '../api/projects';
import { uploadImage, getImageUrlById } from '../api/images';
import ProjectMilestonesSection from './dashboard/ProjectMilestonesSection';
import ClassDescriptionEditor from './dashboard/ClassDescriptionEditor';

interface ProjectModalProps {
  show: boolean;
  onHide: () => void;
  onProjectSaved: () => void;
  project?: Project | null;
  userId: string;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ show, onHide, onProjectSaved, project, userId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    descriptionHtml: '',
    dtStartDate: '',
    dtEndDate: '',
    fundsNeeded: 0,
    fundsRaised: 0,
    isCompleted: false,
    isPublic: false,
    category: 'General',
  });
  const [imageId, setImageId] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        descriptionHtml: project.descriptionHtml ?? '',
        dtStartDate: new Date(project.dtStartDate).toISOString().split('T')[0],
        dtEndDate: new Date(project.dtEndDate).toISOString().split('T')[0],
        fundsNeeded: project.fundsNeeded,
        fundsRaised: project.fundsRaised,
        isCompleted: project.isCompleted,
        isPublic: project.isPublic,
        category: project.category || 'General',
      });
      setImageId(project.imageId ?? null);
      setImageFile(null);
    } else {
      setFormData({
        name: '',
        description: '',
        descriptionHtml: '',
        dtStartDate: '',
        dtEndDate: '',
        fundsNeeded: 0,
        fundsRaised: 0,
        isCompleted: false,
        isPublic: false,
        category: 'General',
      });
      setImageId(null);
      setImageFile(null);
    }
    setError(null);
  }, [project, show]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
          ? parseFloat(value) || 0
          : value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      if (!formData.name.trim()) { setError('Project name is required'); return; }
      if (!formData.dtStartDate || !formData.dtEndDate) { setError('Start date and end date are required'); return; }
      if (new Date(formData.dtStartDate) >= new Date(formData.dtEndDate)) { setError('End date must be after start date'); return; }
      if (formData.fundsNeeded < 0 || formData.fundsRaised < 0) { setError('Funds cannot be negative'); return; }
      if (formData.fundsRaised > formData.fundsNeeded) { setError('Funds raised cannot exceed funds needed'); return; }
      let finalImageId: string | null = imageId;
      if (imageFile) {
        const uploaded = await uploadImage(imageFile);
        finalImageId = uploaded.id;
      }
      let descriptionHtml = formData.descriptionHtml;
      if (!descriptionHtml.trim()) {
        descriptionHtml = formData.descriptionHtml;
      }
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = descriptionHtml;
      const plainDescription = tempDiv.textContent || tempDiv.innerText || '';
      const payload = {
        ...formData,
        description: plainDescription.trim(),
        descriptionHtml: descriptionHtml.trim() || null,
        imageId: finalImageId,
      };
     
      if (project) {
        await updateProject(project.id, payload);
      } else {
        await createProject(payload);
      }
      onProjectSaved();
      handleClose();
    } catch (err) {
      console.error('Project save error:', err);
      setError('An error occurred while saving the project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '', description: '', descriptionHtml: '', dtStartDate: '', dtEndDate: '',
      fundsNeeded: 0, fundsRaised: 0, isCompleted: false, isPublic: false, category: 'General',
    });
    setImageId(null);
    setImageFile(null);
    setError(null);
    onHide();
  };

  const currentImageUrl = imageId ? getImageUrlById(imageId) : null;
  const blobPreviewUrl = useMemo(() => {
    if (!imageFile) return null;
    return URL.createObjectURL(imageFile);
  }, [imageFile]);
  useEffect(() => {
    return () => {
      if (blobPreviewUrl) URL.revokeObjectURL(blobPreviewUrl);
    };
  }, [blobPreviewUrl]);
  const imagePreviewUrl = blobPreviewUrl ?? currentImageUrl;

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-folder me-2"></i>
              {project ? 'Edit Project' : 'Add New Project'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </div>
              )}
              <div className="row">
                <div className="col-md-7">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label"><i className="bi bi-tag me-2"></i>Project Name *</label>
                    <input type="text" className="form-control" id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter project name" required />
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="isCompleted" name="isCompleted" checked={formData.isCompleted} onChange={handleInputChange} />
                        <label className="form-check-label" htmlFor="isCompleted">Completed</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="isPublic" name="isPublic" checked={formData.isPublic} onChange={handleInputChange} />
                        <label className="form-check-label" htmlFor="isPublic">Public</label>
                      </div>
                    </div>
                    <div className="mt-2">
                      <label htmlFor="category" className="form-label mb-1">Category</label>
                      <select
                        id="category"
                        name="category"
                        className="form-select form-select-sm"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        <option value="General">General</option>
                        <option value="Masjid">Masjid</option>
                        <option value="Welfare">Welfare</option>
                        <option value="Education">Education</option>
                        <option value="Operations">Operations</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label"><i className="bi bi-file-text me-2"></i>Description</label>
                <ClassDescriptionEditor
                  key={project?.id ?? 'new'}
                  html={formData.descriptionHtml}
                  onHtmlChange={(html) => setFormData((prev) => ({ ...prev, descriptionHtml: html }))}
                />
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="dtStartDate" className="form-label"><i className="bi bi-calendar-event me-2"></i>Start Date *</label>
                    <input type="date" className="form-control" id="dtStartDate" name="dtStartDate" value={formData.dtStartDate} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="dtEndDate" className="form-label"><i className="bi bi-calendar-check me-2"></i>End Date *</label>
                    <input type="date" className="form-control" id="dtEndDate" name="dtEndDate" value={formData.dtEndDate} onChange={handleInputChange} required />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="fundsNeeded" className="form-label"><i className="bi bi-currency-exchange me-2"></i>Funds Needed (R)</label>
                    <input
                      type="number"
                      className="form-control input-no-spinner"
                      id="fundsNeeded"
                      name="fundsNeeded"
                      value={formData.fundsNeeded}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="fundsRaised" className="form-label"><i className="bi bi-coin me-2"></i>Funds Raised (R)</label>
                    <input
                      type="number"
                      className="form-control input-no-spinner"
                      id="fundsRaised"
                      name="fundsRaised"
                      value={formData.fundsRaised}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                    />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label"><i className="bi bi-image me-2"></i>Project Image</label>
                {imagePreviewUrl && (
                  <div className="mb-2">
                    <img
                      src={imagePreviewUrl}
                      alt="Project"
                      style={{ maxWidth: 160, maxHeight: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  className="form-control"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setImageFile(file);
                  }}
                />
                <small className="text-muted">Optional. PNG, JPEG or JPG. Max 10MB.</small>
                {(imageId || imageFile) && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0 mt-1 d-block"
                    onClick={() => { setImageId(null); setImageFile(null); }}
                  >
                    Remove image
                  </button>
                )}
              </div>
              <div className="alert alert-info" role="alert">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Public Projects:</strong> Projects marked as &quot;Public&quot; will be visible to non-signed in users on the website.
              </div>
              {project && (
                <ProjectMilestonesSection projectId={project.id} />
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isLoading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{project ? 'Updating...' : 'Creating...'}</> : <><i className="bi bi-check-circle me-2"></i>{project ? 'Update Project' : 'Create Project'}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
